import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flask, UploadSimple, X } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { useLabReports, type LabReportRow } from '../../api/lab'
import { useOrgs } from '../../api/reference'
import { ActionButton } from '../../components/Can'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, RefLink, Status, UserName, When } from '../../components/bits'
import { LabReportUploadDialog } from '../../components/lab/LabReportUploadDialog'
import { useUrlState } from '../../lib/useUrlState'
import { OVERALL_LABEL, REPORT_STATUS_LABEL, SAMPLE_KIND_LABEL, UNIT_TYPE_LABEL, unitLink } from '../../model/lab/labText'

const LIMIT = 25

/** Lab reports (F7.5): attach, read rows, second-person check. */
export function LabReportsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({ search: '', status: '', overall: '', labOrgId: '', unitId: '', sampleId: '', page: 1 })
  const labs = useOrgs('LAB')
  const [uploading, setUploading] = useState(false)
  const q = useLabReports({
    page: f.page,
    limit: LIMIT,
    search: f.search || undefined,
    status: f.status || undefined,
    overall: f.overall || undefined,
    labOrgId: f.labOrgId || undefined,
    unitId: f.unitId || undefined,
    sampleId: f.sampleId || undefined,
  })

  const columns: Column<LabReportRow>[] = [
    { id: 'no', header: 'Report', cell: (r) => <Code className="whitespace-nowrap">{r.reportNo}</Code> },
    {
      id: 'unit',
      header: 'Tested',
      cell: (r) => {
        const to = unitLink(r.unitType, r.unitId)
        return (
          <span className="flex flex-col leading-tight">
            {to ? <RefLink to={to}>{r.unitCode ?? '—'}</RefLink> : <Code>{r.unitCode}</Code>}
            <span className="text-[11.5px] text-muted">
              {r.unitType ? (UNIT_TYPE_LABEL[r.unitType] ?? r.unitType) : ''}
              {r.sampleKind ? ` · ${SAMPLE_KIND_LABEL[r.sampleKind] ?? r.sampleKind}` : ''}
            </span>
          </span>
        )
      },
    },
    { id: 'lab', header: 'Lab', cell: (r) => r.labName ?? '—', hideBelow: 'md' },
    { id: 'tested', header: 'Tested on', cell: (r) => <When at={r.testedAt} dateOnly />, hideBelow: 'sm' },
    {
      id: 'rows',
      header: 'Rows',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (r) => (
        <span>
          {r.rowCount}
          {r.rowsNotPass > 0 && <span className="ml-1 text-danger">({r.rowsNotPass} not pass)</span>}
        </span>
      ),
    },
    { id: 'overall', header: 'Overall', cell: (r) => <Status status={r.overall} label={r.overall ? OVERALL_LABEL[r.overall] : undefined} /> },
    { id: 'status', header: 'Check', cell: (r) => <Status status={r.status} label={REPORT_STATUS_LABEL[r.status]} /> },
    {
      id: 'by',
      header: 'Entered / checked by',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="flex flex-col text-[13px] leading-tight">
          <UserName id={r.enteredById} />
          <span className="text-muted">
            <UserName id={r.verifiedById} fallback="Not checked" />
          </span>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Lab reports"
      description="Reports from accredited labs on batch and gate samples. Every row is judged against the limit in force on the test date; a second person checks the rows against the PDF."
      actions={
        <ActionButton perm="lab-reports:create" leadingIcon={<UploadSimple />} onClick={() => setUploading(true)}>
          Upload report
        </ActionButton>
      }
    >
      <DataTable<LabReportRow>
        caption="Lab reports"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/lab-reports/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Report or lot code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. LR-2026" />
            <SelectFilter
              label="Check"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={Object.entries(REPORT_STATUS_LABEL).map(([value, label]) => ({ value, label }))}
            />
            <SelectFilter
              label="Overall"
              value={f.overall}
              onChange={(overall) => setF({ overall })}
              options={Object.entries(OVERALL_LABEL).map(([value, label]) => ({ value, label }))}
            />
            <SelectFilter label="Lab" value={f.labOrgId} onChange={(labOrgId) => setF({ labOrgId })} options={(labs.data ?? []).map((o) => ({ value: o.id, label: o.name }))} />
            {f.unitId && (
              <button type="button" className="inline-flex h-9 items-center gap-1 rounded-chip border border-rule px-2 text-[12.5px]" onClick={() => setF({ unitId: '' })}>
                One lot or batch <X aria-hidden="true" />
              </button>
            )}
            {f.sampleId && (
              <button type="button" className="inline-flex h-9 items-center gap-1 rounded-chip border border-rule px-2 text-[12.5px]" onClick={() => setF({ sampleId: '' })}>
                One sample <X aria-hidden="true" />
              </button>
            )}
          </FilterBar>
        }
        empty={<EmptyState icon={<Flask />} title={active ? 'No reports match' : 'No lab reports yet'} description="Upload a report when it arrives from the lab." />}
      />
      {uploading && <LabReportUploadDialog onClose={() => setUploading(false)} />}
    </ConsolePage>
  )
}
